import { MetaMockup } from '@/components/mockups/MetaMockup';
import { MetaCarouselMockup } from '@/components/mockups/MetaCarouselMockup';
import { MetaCollectionMockup } from '@/components/mockups/MetaCollectionMockup';
import { MetaReelsMockup } from '@/components/mockups/MetaReelsMockup';
import { MetaStoryMockup } from '@/components/mockups/MetaStoryMockup';
import { MetaStoryCarouselMockup } from '@/components/mockups/MetaStoryCarouselMockup';
import { MetaStoryCollectionMockup } from '@/components/mockups/MetaStoryCollectionMockup';
import { TikTokMockup } from '@/components/mockups/TikTokMockup';
import { SnapchatMockup } from '@/components/mockups/SnapchatMockup';
import { SnapchatStoryAdMockup } from '@/components/mockups/SnapchatStoryAdMockup';
import { LinkedInMockup } from '@/components/mockups/LinkedInMockup';
import { LinkedInCarouselMockup } from '@/components/mockups/LinkedInCarouselMockup';
import { LinkedInDocumentMockup } from '@/components/mockups/LinkedInDocumentMockup';
import { type MockupType } from '../types';
import { DUMMY_CAROUSEL_ITEMS } from '../placeholder';

const SHARED = {
  pageName: 'Ford',
  caption: 'Primary text placeholder\u2026',
  ctaText: 'CTA placeholder\u2026',
};

export interface MockupPreviewProps {
  type: MockupType;
  imageUrl1x1?: string;
  imageUrl9x16?: string;
  contentType1x1?: string | null;
  contentType9x16?: string | null;
  metaCaption?: string;
  metaHeadline?: string;
  metaDescription?: string;
  metaCta?: string;
  metaCarouselCaption?: string;
  metaCarouselCta?: string;
  metaCarouselFeedItems?: { imageUrl?: string; contentType?: string | null; headline?: string; description?: string }[];
  metaCarouselStoryItems?: { imageUrl?: string; contentType?: string | null }[];
  onMetaCarouselFeedCardChange?: (index: number) => void;
  onMetaCarouselStoryCardChange?: (index: number) => void;
  metaCarouselFeedActiveIndex?: number;
  metaCarouselStoryActiveIndex?: number;
  snapchatBrand?: string;
  snapchatHeadline?: string;
  snapchatStoryHeadline?: string;
  snapchatStoryItems?: { imageUrl?: string; headline: string }[];
  snapchatActiveIndex?: number;
  onSnapchatSlideChange?: (index: number) => void;
  linkedInImageUrl?: string;
  linkedInCaption?: string;
  linkedInHeadline?: string;
  linkedInCta?: string;
  linkedInWebsite?: string;
  linkedInDocCaption?: string;
  linkedInDocHeadline?: string;
  linkedInDocPageImage?: string | null;
  linkedInCarouselCaption?: string;
  linkedInCarouselItems?: { imageUrl?: string; headline: string }[];
  linkedInCarouselActiveIndex?: number;
  onLinkedInCarouselCardChange?: (index: number) => void;
  collectionCaption?: string;
  collectionProducts?: { imageUrl?: string; title?: string }[];
  collectionCta?: string;
  collectionWebsite?: string;
  tikTokCaption?: string;
  tikTokCta?: string;
}

export function MockupPreview({
  type, imageUrl1x1, imageUrl9x16, contentType1x1, contentType9x16,
  metaCaption, metaHeadline, metaDescription, metaCta,
  metaCarouselCaption, metaCarouselCta, metaCarouselFeedItems, metaCarouselStoryItems, onMetaCarouselFeedCardChange, onMetaCarouselStoryCardChange,
  metaCarouselFeedActiveIndex, metaCarouselStoryActiveIndex,
  snapchatHeadline, snapchatStoryHeadline,
  snapchatStoryItems, snapchatActiveIndex, onSnapchatSlideChange,
  linkedInImageUrl, linkedInCaption, linkedInHeadline, linkedInCta, linkedInWebsite,
  linkedInDocCaption, linkedInDocHeadline, linkedInDocPageImage,
  linkedInCarouselCaption, linkedInCarouselItems, linkedInCarouselActiveIndex, onLinkedInCarouselCardChange,
  collectionCaption, collectionProducts, collectionCta, collectionWebsite,
  tikTokCaption, tikTokCta,
}: MockupPreviewProps) {
  const mockup = (() => {
    switch (type) {
      case 'meta':                  return <MetaMockup pageName={SHARED.pageName} caption={metaCaption || SHARED.caption} headline={metaHeadline || undefined} description={metaDescription || undefined} ctaText={metaCta || SHARED.ctaText} imageUrl={imageUrl1x1} contentType={contentType1x1} />;
      case 'meta-carousel':         return <MetaCarouselMockup pageName="Ford" caption={metaCarouselCaption || SHARED.caption} ctaText={metaCarouselCta || SHARED.ctaText} items={metaCarouselFeedItems?.length ? metaCarouselFeedItems : DUMMY_CAROUSEL_ITEMS} onVisibleCardChange={onMetaCarouselFeedCardChange} activeIndex={metaCarouselFeedActiveIndex} />;
      case 'meta-collection':       return <MetaCollectionMockup pageName={SHARED.pageName} caption={collectionCaption || SHARED.caption} heroImageUrl={imageUrl1x1} products={collectionProducts} ctaText={collectionCta || SHARED.ctaText} />;
      case 'meta-reels':            return <MetaReelsMockup caption={metaCaption || SHARED.caption} ctaText={metaCta || SHARED.ctaText} imageUrl={imageUrl9x16} contentType={contentType9x16} />;
      case 'meta-story':            return <MetaStoryMockup caption={metaCaption || SHARED.caption} ctaText={metaCta || SHARED.ctaText} imageUrl={imageUrl9x16} contentType={contentType9x16} />;
      case 'meta-story-carousel':   return <MetaStoryCarouselMockup username="Ford" ctaText={metaCarouselCta || SHARED.ctaText} items={metaCarouselStoryItems?.length ? metaCarouselStoryItems : undefined} onSlideChange={onMetaCarouselStoryCardChange} activeIndex={metaCarouselStoryActiveIndex} />;
      case 'meta-story-collection': return <MetaStoryCollectionMockup username={SHARED.pageName} heroImageUrl={imageUrl1x1} products={collectionProducts} ctaText={collectionCta || SHARED.ctaText} websiteUrl={collectionWebsite || 'ford.com'} />;
      case 'tiktok':                return <TikTokMockup imageUrl={imageUrl9x16} contentType={contentType9x16} caption={tikTokCaption || SHARED.caption} ctaText={tikTokCta || SHARED.ctaText} />;
      case 'snapchat':              return <SnapchatMockup headline={snapchatHeadline || 'Headline placeholder\u2026'} imageUrl={imageUrl9x16} contentType={contentType9x16} />;
      case 'snapchat-story':        return <SnapchatStoryAdMockup items={(snapchatStoryItems ?? []).map(item => ({ ...item, headline: snapchatStoryHeadline || 'Headline placeholder\u2026' }))} activeIndex={snapchatActiveIndex} onSlideChange={onSnapchatSlideChange} />;
      case 'linkedin':              return <LinkedInMockup pageName="Ford" imageUrl={linkedInImageUrl} caption={linkedInCaption || 'Primary text placeholder\u2026'} tagline={linkedInHeadline || 'Headline placeholder\u2026'} ctaText={linkedInCta || 'CTA placeholder\u2026'} website={linkedInWebsite || 'ford.com'} />;
      case 'linkedin-carousel':     return <LinkedInCarouselMockup pageName="Ford" caption={linkedInCarouselCaption || 'Primary text placeholder\u2026'} items={linkedInCarouselItems?.length ? linkedInCarouselItems : DUMMY_CAROUSEL_ITEMS} activeIndex={linkedInCarouselActiveIndex} onVisibleCardChange={onLinkedInCarouselCardChange} />;
      case 'linkedin-document':     return <LinkedInDocumentMockup pageName="Ford" caption={linkedInDocCaption || 'Primary text placeholder\u2026'} headline={linkedInDocHeadline || 'Headline placeholder\u2026'} pageImages={linkedInDocPageImage ? [linkedInDocPageImage] : []} />;
      default:                      return <MetaMockup pageName={SHARED.pageName} caption={metaCaption || SHARED.caption} headline={metaHeadline || undefined} description={metaDescription || undefined} ctaText={metaCta || SHARED.ctaText} imageUrl={imageUrl1x1} />;
    }
  })();

  return mockup;
}
